import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AwsServiceIcon from './AwsServiceIcon';
import AwsServiceSelect from './AwsServiceSelect';
import './AlarmManagement.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const AlarmManagement = ({ teamName, environment, accountId, region }) => {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [resources, setResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [showAlarmDetails, setShowAlarmDetails] = useState(false);
  const [resourceSearch, setResourceSearch] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    service: 'lambda',
    metricName: '',
    threshold: '',
    comparisonOperator: 'GreaterThanThreshold',
    statistic: 'Sum',
    period: 300,
    evaluationPeriods: 1,
    datapointsToAlarm: 1,
    dimensionType: 'all',
    selectedResources: []
  });

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Service options
  const services = [
    // Labels are plain text; the official AWS service icon is rendered by
    // <AwsServiceIcon> / <AwsServiceSelect> keyed on `value`.
    { value: 'lambda', label: 'AWS Lambda' },
    { value: 'ec2', label: 'Amazon EC2' },
    { value: 'rds', label: 'Amazon RDS' },
    { value: 'dynamodb', label: 'Amazon DynamoDB' },
    { value: 'apigateway', label: 'API Gateway' },
    { value: 'alb', label: 'Application Load Balancer' },
    { value: 'sqs', label: 'Amazon SQS' },
    { value: 'sns', label: 'Amazon SNS' },
    { value: 's3', label: 'Amazon S3' },
    { value: 'cloudfront', label: 'Amazon CloudFront' },
    { value: 'ecs', label: 'Amazon ECS' },
    { value: 'stepfunctions', label: 'AWS Step Functions' },
    { value: 'eventbridge', label: 'Amazon EventBridge' },
    { value: 'ecr', label: 'Amazon ECR' },
    { value: 'secretsmanager', label: 'Secrets Manager' },
    { value: 'elasticache', label: 'Amazon ElastiCache' },
    { value: 'kinesis', label: 'Amazon Kinesis' },
    { value: 'glue', label: 'AWS Glue' },
    { value: 'mwaa', label: 'Amazon MWAA (Airflow)' }
  ];

  // Comparison operators
  const comparisonOperators = [
    { value: 'GreaterThanThreshold', label: 'Greater than (>)' },
    { value: 'GreaterThanOrEqualToThreshold', label: 'Greater than or equal (≥)' },
    { value: 'LessThanThreshold', label: 'Less than (<)' },
    { value: 'LessThanOrEqualToThreshold', label: 'Less than or equal (≤)' }
  ];

  // Statistics
  const statistics = [
    { value: 'Sum', label: 'Sum' },
    { value: 'Average', label: 'Average' },
    { value: 'Maximum', label: 'Maximum' },
    { value: 'Minimum', label: 'Minimum' },
    { value: 'SampleCount', label: 'Sample Count' }
  ];

  // Period options (in seconds)
  const periods = [
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' },
    { value: 900, label: '15 minutes' },
    { value: 3600, label: '1 hour' },
    { value: 21600, label: '6 hours' }
  ];

  useEffect(() => {
    fetchAlarms();
    // Re-fetch alarms only when the target account changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamName, environment, accountId, region]);

  useEffect(() => {
    if (formData.service) {
      fetchServiceMetrics(formData.service);
      // Fetch resources when service changes or when "select" dimension type is chosen
      if (formData.dimensionType === 'select') {
        fetchResources(formData.service);
      }
    }
    // Intentionally keyed on the selected service only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.service]);

  // Fetch resources when dimension type changes to "select"
  useEffect(() => {
    if (formData.dimensionType === 'select' && formData.service) {
      fetchResources(formData.service);
    }
    // Intentionally keyed on the dimension type only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dimensionType]);

  const fetchAlarms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/account/${teamName}/${environment}/alarms`,
        {
          params: { accountId, region }
        }
      );
      setAlarms(response.data.alarms || []);
    } catch (err) {
      console.error('Error fetching alarms:', err);
      setError('Failed to load alarms');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceMetrics = async (service) => {
    try {
      const response = await axios.get(`${API_URL}/api/alarm-metrics/${service}`);
      setAvailableMetrics(response.data.metrics?.metrics || []);
      
      // Set default metric if available
      if (response.data.metrics?.metrics?.length > 0) {
        setFormData(prev => ({
          ...prev,
          metricName: response.data.metrics.metrics[0].name,
          statistic: response.data.metrics.metrics[0].statistics[0]
        }));
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
    }
  };

  const fetchResources = async (service) => {
    setLoadingResources(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/account/${teamName}/${environment}/resources/${service}`,
        {
          params: { accountId, region }
        }
      );
      setResources(response.data.resources || []);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setResources([]);
    } finally {
      setLoadingResources(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If metric changes, update default statistic
    if (field === 'metricName') {
      const metric = availableMetrics.find(m => m.name === value);
      if (metric && metric.statistics.length > 0) {
        setFormData(prev => ({ ...prev, statistic: metric.statistics[0] }));
      }
    }

    if (field === 'dimensionType' && value !== 'select') {
      setResourceSearch('');
    }
  };

  const addSelectedResource = (resourceName) => {
    if (!resourceName) return;
    if (!formData.selectedResources.includes(resourceName)) {
      handleInputChange('selectedResources', [...formData.selectedResources, resourceName]);
    }
  };

  const handleAddResourceFromSearch = () => {
    const query = resourceSearch.trim().toLowerCase();
    if (!query) return;

    const exactMatch = resources.find(r => r.name.toLowerCase() === query);
    const partialMatch = resources.find(r => r.name.toLowerCase().includes(query));
    const selected = exactMatch || partialMatch;

    if (selected) {
      addSelectedResource(selected.name);
      setResourceSearch('');
    }
  };

  const filteredResources = resources.filter(resource =>
    resource.name.toLowerCase().includes(resourceSearch.trim().toLowerCase())
  );

  const handleCreateAlarm = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      // Validate form
      if (!formData.metricName || !formData.threshold) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      const payload = {
        accountId,
        region,
        service: formData.service,
        metricName: formData.metricName,
        threshold: parseFloat(formData.threshold),
        comparisonOperator: formData.comparisonOperator,
        statistic: formData.statistic,
        period: parseInt(formData.period),
        evaluationPeriods: parseInt(formData.evaluationPeriods),
        datapointsToAlarm: parseInt(formData.datapointsToAlarm),
        dimensions: formData.dimensionType === 'all' ? 'all' : formData.selectedResources
      };

      const response = await axios.post(
        `${API_URL}/api/account/${teamName}/${environment}/alarms`,
        payload
      );

      // Handle response - check for async (202) or sync response
      let result;
      try {
        result = typeof response.data.body === 'string' 
          ? JSON.parse(response.data.body) 
          : response.data;
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        result = response.data;
      }
      
      // Handle async response (202 Accepted)
      if (response.status === 202 || result.jobId) {
        setSuccess(`Alarm creation started in background (Job ID: ${result.jobId}). Alarms will appear shortly - refresh in a few seconds.`);
        setShowCreateForm(false);
        
        // Auto-refresh alarms after a delay
        setTimeout(() => {
          fetchAlarms();
        }, 5000);  // Refresh after 5 seconds
        
        // Reset form
        setFormData({
          service: 'lambda',
          metricName: '',
          threshold: '',
          comparisonOperator: 'GreaterThanThreshold',
          statistic: 'Sum',
          period: 300,
          evaluationPeriods: 1,
          datapointsToAlarm: 1,
          dimensionType: 'all',
          selectedResources: []
        });
      } else if (result.success) {
        const operation = result.operation === 'updated' ? 'updated' : 'created';
        setSuccess(`Successfully ${operation} ${result.alarmsCreated} alarm(s)`);
        setShowCreateForm(false);
        fetchAlarms();
        
        // Reset form
        setFormData({
          service: 'lambda',
          metricName: '',
          threshold: '',
          comparisonOperator: 'GreaterThanThreshold',
          statistic: 'Sum',
          period: 300,
          evaluationPeriods: 1,
          datapointsToAlarm: 1,
          dimensionType: 'all',
          selectedResources: []
        });
      } else {
        // Show error message from backend
        const errorMsg = result.error || 'Failed to create alarm';
        setError(errorMsg);
        console.error('Alarm creation failed:', errorMsg);
      }
    } catch (err) {
      console.error('Error creating alarm:', err);
      
      // Try to extract error message from response
      let errorMessage = 'Failed to create alarm';
      
      if (err.response?.data) {
        try {
          const errorData = typeof err.response.data === 'string' 
            ? JSON.parse(err.response.data) 
            : err.response.data;
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          errorMessage = err.response.data.error || err.response.data.message || err.message || errorMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAlarmClick = (alarm) => {
    setSelectedAlarm(alarm);
    setShowAlarmDetails(true);
  };

  const handleCloseDetails = () => {
    setSelectedAlarm(null);
    setShowAlarmDetails(false);
  };

  const getSelectedMetric = () => {
    return availableMetrics.find(m => m.name === formData.metricName);
  };

  return (
    <div className="alarm-management">
      <div className="alarm-header">
        <div className="alarm-title-section">
          <h3 className="alarm-title">🔔 CloudWatch Alarm Management</h3>
          <p className="alarm-subtitle">Create and manage CloudWatch alarms for this account</p>
        </div>
        <button 
          className="btn-create-alarm"
          onClick={() => setShowCreateForm(!showCreateForm)}
          disabled={loading}
        >
          {showCreateForm ? '✕ Cancel' : '+ Create Alarm'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          <span>{success}</span>
          <button className="alert-close" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {showCreateForm && (
        <div className="alarm-create-form">
          <h4 className="form-title">Create New Alarm</h4>
          
          <div className="form-grid">
            {/* Service Selection */}
            <div className="form-group">
              <label className="form-label">
                Service <span className="required">*</span>
              </label>
              <AwsServiceSelect
                options={services}
                value={formData.service}
                onChange={(val) => handleInputChange('service', val)}
              />
            </div>

            {/* Metric Selection */}
            <div className="form-group">
              <label className="form-label">
                Metric <span className="required">*</span>
              </label>
              <select
                className="form-select"
                value={formData.metricName}
                onChange={(e) => handleInputChange('metricName', e.target.value)}
              >
                <option value="">Select metric...</option>
                {availableMetrics.map(metric => (
                  <option key={metric.name} value={metric.name}>
                    {metric.name}
                  </option>
                ))}
              </select>
              {getSelectedMetric() && (
                <div className="metric-info">
                  <span className="form-hint">{getSelectedMetric().description}</span>
                  <span className="form-hint">Unit: {getSelectedMetric().unit}</span>
                </div>
              )}
            </div>

            {/* Threshold */}
            <div className="form-group">
              <label className="form-label">
                Threshold <span className="required">*</span>
              </label>
              <input
                type="number"
                className="form-input"
                value={formData.threshold}
                onChange={(e) => handleInputChange('threshold', e.target.value)}
                placeholder="e.g., 10"
                step="any"
              />
            </div>

            {/* Comparison Operator */}
            <div className="form-group">
              <label className="form-label">Comparison Operator</label>
              <select
                className="form-select"
                value={formData.comparisonOperator}
                onChange={(e) => handleInputChange('comparisonOperator', e.target.value)}
              >
                {comparisonOperators.map(op => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Statistic */}
            <div className="form-group">
              <label className="form-label">Statistic</label>
              <select
                className="form-select"
                value={formData.statistic}
                onChange={(e) => handleInputChange('statistic', e.target.value)}
              >
                {getSelectedMetric()?.statistics.map(stat => (
                  <option key={stat} value={stat}>
                    {stat}
                  </option>
                )) || statistics.map(stat => (
                  <option key={stat.value} value={stat.value}>
                    {stat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Period */}
            <div className="form-group">
              <label className="form-label">Period</label>
              <select
                className="form-select"
                value={formData.period}
                onChange={(e) => handleInputChange('period', e.target.value)}
              >
                {periods.map(period => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Evaluation Periods */}
            <div className="form-group">
              <label className="form-label">Evaluation Periods</label>
              <input
                type="number"
                className="form-input"
                value={formData.evaluationPeriods}
                onChange={(e) => handleInputChange('evaluationPeriods', e.target.value)}
                min="1"
                max="10"
              />
              <span className="form-hint">Number of periods to evaluate</span>
            </div>

            {/* Datapoints to Alarm */}
            <div className="form-group">
              <label className="form-label">Datapoints to Alarm</label>
              <input
                type="number"
                className="form-input"
                value={formData.datapointsToAlarm}
                onChange={(e) => handleInputChange('datapointsToAlarm', e.target.value)}
                min="1"
                max={formData.evaluationPeriods}
              />
              <span className="form-hint">
                Out of {formData.evaluationPeriods} evaluation periods
              </span>
            </div>
          </div>

          {/* Dimension Selection */}
          <div className="form-group-full">
            <label className="form-label">Target Resources</label>
            <div className="dimension-selector">
              <label className="radio-label">
                <input
                  type="radio"
                  name="dimensionType"
                  value="all"
                  checked={formData.dimensionType === 'all'}
                  onChange={(e) => handleInputChange('dimensionType', e.target.value)}
                />
                <span className="radio-text">
                  <strong>All Resources</strong>
                  <span className="radio-hint">
                    Create alarm for all {formData.service} resources in this account
                  </span>
                </span>
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="dimensionType"
                  value="select"
                  checked={formData.dimensionType === 'select'}
                  onChange={(e) => handleInputChange('dimensionType', e.target.value)}
                />
                <span className="radio-text">
                  <strong>Select from List</strong>
                  <span className="radio-hint">
                    Choose specific resources from a list
                  </span>
                </span>
              </label>
            </div>

            {/* Resource Selection List */}
            {formData.dimensionType === 'select' && (
              <div className="resource-selector">
                {loadingResources ? (
                  <div className="loading-resources">
                    <div className="spinner-small"></div>
                    <span>Loading {formData.service} resources...</span>
                  </div>
                ) : resources.length === 0 ? (
                  <div className="no-resources">
                    <p>No {formData.service} resources found in this account</p>
                  </div>
                ) : (
                  <div className="resource-list">
                    <div className="resource-list-header">
                      <span>Select resources ({formData.selectedResources.length} selected)</span>
                      <div className="resource-actions">
                        <button
                          type="button"
                          className="btn-select-all"
                          onClick={() => handleInputChange('selectedResources', resources.map(r => r.name))}
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          className="btn-clear-all"
                          onClick={() => handleInputChange('selectedResources', [])}
                        >
                          Clear All
                        </button>
                      </div>
                    </div>

                    <div className="resource-search-row">
                      <input
                        type="text"
                        className="form-input resource-search-input"
                        list={`resource-options-${formData.service}`}
                        placeholder={`Search ${formData.service} resources...`}
                        value={resourceSearch}
                        onChange={(e) => setResourceSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddResourceFromSearch();
                          }
                        }}
                      />
                      <datalist id={`resource-options-${formData.service}`}>
                        {filteredResources.map((resource) => (
                          <option key={resource.name} value={resource.name} />
                        ))}
                      </datalist>
                      <button
                        type="button"
                        className="btn-select-all"
                        onClick={handleAddResourceFromSearch}
                        disabled={!resourceSearch.trim()}
                      >
                        Add
                      </button>
                    </div>

                    <div className="resource-search-hint">
                      {filteredResources.length} resource(s) match your search
                    </div>

                    <div className="selected-resource-tags">
                      {formData.selectedResources.length === 0 ? (
                        <span className="no-selected-resources">No resources selected yet</span>
                      ) : (
                        formData.selectedResources.map((resourceName) => {
                          const resource = resources.find(r => r.name === resourceName);
                          return (
                            <div key={resourceName} className="selected-resource-tag">
                              <div className="selected-resource-meta">
                                <span className="resource-name">{resourceName}</span>
                                {resource?.arn && <span className="resource-arn">{resource.arn}</span>}
                              </div>
                              <button
                                type="button"
                                className="remove-resource-btn"
                                onClick={() => handleInputChange(
                                  'selectedResources',
                                  formData.selectedResources.filter(r => r !== resourceName)
                                )}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              className="btn-cancel"
              onClick={() => setShowCreateForm(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="btn-submit"
              onClick={handleCreateAlarm}
              disabled={loading || !formData.metricName || !formData.threshold}
            >
              {loading ? 'Creating...' : 'Create Alarm'}
            </button>
          </div>
        </div>
      )}

      {/* Existing Alarms List */}
      <div className="alarms-list-section">
        <h4 className="section-title">Existing Alarms ({alarms.length})</h4>
        
        {loading && !showCreateForm ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading alarms...</p>
          </div>
        ) : alarms.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔔</div>
            <h5>No Alarms Created</h5>
            <p>Create your first CloudWatch alarm to start monitoring your resources</p>
          </div>
        ) : (
          <div className="alarm-cards-grid">
            {alarms.map(alarm => (
              <div 
                key={alarm.alarmId} 
                className="alarm-card clickable"
                onClick={() => handleAlarmClick(alarm)}
                title="Click to view alarm details"
              >
                <div className="alarm-card-header">
                  <div className="alarm-service-badge">
                    <AwsServiceIcon service={alarm.service} size={16} /> {alarm.service?.toUpperCase() || 'UNKNOWN'}
                  </div>
                  <div className="alarm-count-badge" title="Number of CloudWatch alarms">
                    🔔 {alarm.alarmCount || alarm.alarmNames?.length || 0}
                  </div>
                </div>
                
                <div className="alarm-card-body">
                  <h5 className="alarm-metric-name">{alarm.metricName || 'Unknown Metric'}</h5>
                  
                  <div className="alarm-details">
                    <div className="alarm-detail-row">
                      <span className="detail-label">Threshold:</span>
                      <span className="detail-value">
                        {alarm.comparisonOperator?.replace('Threshold', '').replace('Than', ' than ').replace('To', ' to ') || '>'} {alarm.threshold || 0}
                      </span>
                    </div>
                    
                    <div className="alarm-detail-row">
                      <span className="detail-label">Statistic:</span>
                      <span className="detail-value">{alarm.statistic || 'N/A'}</span>
                    </div>
                    
                    <div className="alarm-detail-row">
                      <span className="detail-label">Period:</span>
                      <span className="detail-value">
                        {alarm.period >= 3600 ? `${alarm.period / 3600}h` : alarm.period >= 60 ? `${alarm.period / 60}m` : `${alarm.period}s`}
                      </span>
                    </div>
                    
                    <div className="alarm-detail-row">
                      <span className="detail-label">Evaluation:</span>
                      <span className="detail-value">
                        {alarm.datapointsToAlarm || 0} of {alarm.evaluationPeriods || 0} periods
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="alarm-card-footer">
                  <span className="alarm-created">
                    Created: {alarm.createdAt ? new Date(alarm.createdAt * 1000).toLocaleDateString() : 'Unknown'}
                  </span>
                  <span className={`alarm-status ${alarm.status || 'inactive'}`}>
                    {alarm.status === 'active' ? '✓ Active' : '⚠ Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alarm Details Modal */}
      {showAlarmDetails && selectedAlarm && (
        <div className="modal-overlay" onClick={handleCloseDetails}>
          <div className="modal-content alarm-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <AwsServiceIcon service={selectedAlarm.service} size={18} /> {selectedAlarm.metricName} Alarms
              </h3>
              <button className="modal-close" onClick={handleCloseDetails}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="alarm-config-summary">
                <h4>Configuration</h4>
                <div className="config-grid">
                  <div className="config-item">
                    <span className="config-label">Service:</span>
                    <span className="config-value">{selectedAlarm.service?.toUpperCase()}</span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Metric:</span>
                    <span className="config-value">{selectedAlarm.metricName}</span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Threshold:</span>
                    <span className="config-value">
                      {selectedAlarm.comparisonOperator?.replace('Threshold', '').replace('Than', ' than ').replace('To', ' to ')} {selectedAlarm.threshold}
                    </span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Statistic:</span>
                    <span className="config-value">{selectedAlarm.statistic}</span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Period:</span>
                    <span className="config-value">
                      {selectedAlarm.period >= 3600 ? `${selectedAlarm.period / 3600} hour(s)` : 
                       selectedAlarm.period >= 60 ? `${selectedAlarm.period / 60} minute(s)` : 
                       `${selectedAlarm.period} second(s)`}
                    </span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Evaluation:</span>
                    <span className="config-value">
                      {selectedAlarm.datapointsToAlarm} of {selectedAlarm.evaluationPeriods} periods
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Alarm Names List */}
              <div className="alarm-names-section">
                <h4>
                  CloudWatch Alarms ({selectedAlarm.alarmCount || selectedAlarm.alarmNames?.length || 0})
                </h4>
                <div className="alarm-names-list">
                  {(selectedAlarm.alarmNames && selectedAlarm.alarmNames.length > 0) ? (
                    selectedAlarm.alarmNames.map((alarmName, index) => (
                      <div key={index} className="alarm-name-item">
                        <span className="alarm-name-icon">🔔</span>
                        <span className="alarm-name-text">{alarmName}</span>
                      </div>
                    ))
                  ) : (
                    <div className="no-alarm-names">
                      <span>No alarm names available</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-modal-close" onClick={handleCloseDetails}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlarmManagement;
